import React from 'react';
import { Observable } from '@reactivex/rxjs';
import styles from './styles.less';
import classNames from 'classnames';
import Table from 'react-virtualized/dist/commonjs/Table/Table';
import Column from 'react-virtualized/dist/commonjs/Table/Column';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer/AutoSizer';
import defaultRowRenderer from 'react-virtualized/dist/commonjs/Table/defaultRowRenderer';
import mapPropsStream from 'react-recompose/mapPropsStream';
import createEventHandler from 'react-recompose/createEventHandler';
import { withHistogramContainer } from '@graphistry/viz/src/containers/histograms';
import { IconElt } from '@graphistry/viz/src/components/icon/icon-helpers';

const withLegendInteractions = mapPropsStream(props => {
    const { stream: touchMoves, handler: onTouchMove } = createEventHandler();
    const { stream: touchStarts, handler: onTouchStart } = createEventHandler();
    const { stream: touchCancels, handler: onTouchCancel } = createEventHandler();

    const histogramEvents = Observable.merge(
        touchMoves.filter(notInfilled).withLatestFrom(props, pluckActionNamed('binTouchMove')),
        touchStarts.filter(notInfilled).withLatestFrom(props, pluckActionNamed('binTouchStart')),
        touchCancels.filter(notInfilled).withLatestFrom(props, pluckActionNamed('binTouchCancel'))
    )
        .do(({ event, binID, range, action }) =>
            action({
                event,
                binID,
                range,
                binIsFiltered: isBinFiltered(range, binID)
            })
        )
        .ignoreElements();

    return Observable.from(props)
        .merge(histogramEvents)
        .map(props => ({
            ...props,
            onTouchMove,
            onTouchStart,
            onTouchCancel
        }));

    function notInfilled({ rowData }) {
        return rowData && !rowData.infill;
    }
    function pluckActionNamed(actionName) {
        return function({ event, index }, { range, [actionName]: action }) {
            return { action, event, binID: index, range };
        };
    }
});

export const LegendBody = withHistogramContainer(
    withLegendInteractions(
        ({
            name,
            bins: binsWithNulls,
            iconEncodings,
            iconsOn,
            colorEncodings,
            colorsOn,
            sizeEncodings,
            sizesOn,
            clearHighlight,
            onTouchMove,
            onTouchStart,
            onTouchCancel,
            range,
            filter,
            componentType,
            id,
            rowRenderer,
            loading = false
        }) => {
            //console.debug('@LegendBody', {name, binsWithNulls, iconEncodings, iconsOn, colorEncodings, colorsOn, sizeEncodings, sizesOn, clearHighlight, onTouchMove, onTouchStart, onTouchCancel, range, filter, componentType, id, rowRenderer});

            const isPivot = name === 'Pivot';

            const bins = expandPivotBins(isPivot, binsWithNulls);

            if (bins == null) {
                return null;
            }

            const oldBinNames = !(binsWithNulls && binsWithNulls.map)
                ? []
                : binsWithNulls.map(({ count, values }) => values && values[0]);

            range = (filter && range) || [];
            //const enabled = !filter || filter.enabled;
            //const filtered = range && range.length > 0;
            const legendBodyHeight = (Math.min(bins.length, 7) + 1) * 28;

            const sizeNormalizer = makeSizeNormalizer({sizeEncodings, sizesOn});
            const colorCellRenderer = makeColorCellRenderer({
                sizeNormalizer, sizesOn, colorsOn, sizeEncodings, colorEncodings
            });

            return (
                <div
                    className={styles['legendBody']}
                    style={{ maxHeight: legendBodyHeight, minHeight: legendBodyHeight }}
                    onMouseLeave={event => clearHighlight({ event, id, componentType })}>
                    <AutoSizer>
                        {({ width, height }) => (
                            <Table
                                width={width}
                                height={height}
                                rowHeight={28}
                                headerHeight={28}
                                rowCount={bins.length}
                                rowRenderer={customRowRenderer}
                                rowGetter={({ index }) => bins[index] || { values: [] }}>
                                <Column
                                    label="Color"
                                    dataKey="color"
                                    width={1}
                                    flexGrow={1}
                                    className={classNames({
                                        [styles['columnCell']]: true,
                                        [styles['columnEmpty']]: !colorsOn && !sizesOn
                                    })}
                                    headerClassName={classNames({
                                        [styles['columnHeader']]: true,
                                        [styles['columnEmpty']]: !colorsOn && !sizesOn
                                    })}
                                    cellRenderer={colorCellRenderer}
                                />
                                <Column
                                    label={name}
                                    dataKey={name}
                                    width={45}
                                    flexGrow={1}
                                    flexShrink={0}
                                    className={styles['columnCell']}
                                    headerClassName={styles['columnHeader']}
                                    cellRenderer={({ rowData }) => rowData.values[0]}
                                />
                                {isPivot ? null : (
                                    <Column
                                        label="Icon"
                                        dataKey="icon"
                                        width={1}
                                        flexGrow={1}
                                        className={classNames({
                                            [styles['columnCell']]: true,
                                            [styles['columnEmpty']]: !iconsOn
                                        })}
                                        headerClassName={classNames({
                                            [styles['columnHeader']]: true,
                                            [styles['columnEmpty']]: !iconsOn
                                        })}
                                        cellRenderer={iconCellRenderer}
                                    />
                                )}
                                <Column
                                    label="Count"
                                    dataKey="count"
                                    width={1}
                                    flexGrow={1}
                                    className={classNames({
                                        [styles['right']]: true,
                                        [styles['columnCell']]: true
                                    })}
                                    headerClassName={styles['columnHeader']}
                                />
                            </Table>
                        )}
                    </AutoSizer>
                </div>
            );

            function iconCellRenderer({ rowData }) {
                const binName = rowData.values[0];
                const specialIcon = iconsOn
                    ? getMapping(iconEncodings, binName, 'icons')
                    : undefined;
                const specialIconOn = iconsOn && specialIcon !== undefined;
                return specialIconOn 
                    ? (
                        <div className={classNames('fa-fw', styles['icon-container'])}>
                            <IconElt iconClass={specialIcon} />
                        </div>
                    )
                    : <span>&mdash;</span>;
            }

            function customRowRenderer(props) {
                const { rowData } = props;
                const binName = rowData.values[0];
                const index = oldBinNames.indexOf(binName);
                const row = defaultRowRenderer(props);
                return React.cloneElement(row, {
                    ...row.props,
                    onMouseDown: event => !loading && onTouchStart({ event, index, rowData }),
                    onTouchStart: event => !loading && onTouchStart({ event, index, rowData }),
                    onTouchMove: event => !loading && onTouchMove({ event, index, rowData }),
                    onMouseMove: event => !loading && onTouchMove({ event, index, rowData }),
                    onTouchCancel: event => !loading && onTouchCancel({ event, index, rowData }),
                    className:
                        row.props.className +
                        ' ' +
                        classNames({
                            [styles['histobin']]: true,
                            [styles['filteredbins']]: !!range.length,
                            [styles['activebin']]: isBinFiltered(range, index),
                            [styles['infill']]: rowData.infill
                        })
                });
            }
        }
    )
);

function isBinFiltered(range, binID) {
    return range && range.length > 0 && !!~range.indexOf(binID);
}

//bool * ?[ ?{ values: [ int ], count: int } ]
// -> ?[ ?{ values: [ int ], count: int } ]
// Some pivots may have no values because Falcor streaming or no results,
//  so fill them in (or bail)
function expandPivotBins(isPivot, bins) {
    if (bins === null || !isPivot || !bins.length) {
        return bins;
    }

    for (let i = 0; i < bins.length; i++) {
        if (!bins[i]) {
            return null;
        }
    }

    const maxPivot = bins.reduce((acc, { values: [v] }, i) => Math.max(v, acc), bins[0].values[0]);

    return Array.apply(null, { length: maxPivot })
        .map(Function.call, Number)
        .map((_, i) => {
            const pivot = bins.filter(({ values: [pivot] }) => pivot === i + 1);
            return pivot.length
                ? pivot[0]
                : { values: [i + 1], count: 0, exclude: false, infill: true };
        });
}

function getMapping(encodings, binName, encodingType) {
    //console.debug('getMapping', {encodings, binName, encodingType});
    const viaFixedCategoricalMapping =
        encodings.mapping && encodings.mapping.categorical && encodings.mapping.categorical.fixed;

    const viaBins =
        !viaFixedCategoricalMapping &&
        encodings.binning &&
        encodings.binning.bins &&
        encodings.binning.bins instanceof Array;

    if (viaBins) {
        if (encodingType === 'icons') {
            return binName; //ignores bin
        }
        for (let binIndex = 0; binIndex < encodings.binning.bins.length; binIndex++) {
            const bin = encodings.binning.bins[binIndex];
            for (let valIndex = 0; valIndex < bin.values.length; valIndex++) {
                if (bin.values[valIndex] === binName) {
                    const val = encodings.legend[binIndex];
                    return encodingType === 'sizes' ? val * 2 : val;
                }
            }
        }
        return undefined;
    } else if (viaFixedCategoricalMapping) {
        return encodings.mapping.categorical.fixed[binName] || encodings.mapping.categorical.other;
    } else {
        return undefined;
    }
}


function makeColorCellRenderer({sizeNormalizer, sizesOn, colorsOn, sizeEncodings, colorEncodings}) {      
    //console.debug('makeColorCellRenderer', {sizeNormalizer, sizesOn, colorsOn, sizeEncodings, colorEncodings});
    return ({ rowData }) => {
        const binName = rowData.values[0];
        const maybeSize = sizesOn ? sizeNormalizer(getMapping(sizeEncodings, binName, 'sizes')) : undefined;
        const specialSize = sizesOn && (maybeSize !== undefined)
            ? 0.5 + 1.5 * maybeSize.toFixed(1)
            : undefined;
        const specialSizeOn = sizesOn && specialSize !== undefined;
        const specialColor = colorsOn
            ? getMapping(colorEncodings, binName, 'colors')
            : undefined;
        const specialColorOn = colorsOn && specialColor !== undefined;
        const renderedColorStyle = specialColorOn ? { backgroundColor: specialColor } : {};
        const renderedSizeStyle = specialSizeOn
            ? {
                height: `${specialSize}em`,
                width: `${specialSize}em`
            }
            : colorsOn ? { height: '2em', width: '2em' } : { height: '0', width: '0' };
        /*
        console.debug(
            'makeColorCellRenderer', 
            {sizeNormalizer, sizesOn, colorsOn, sizeEncodings, colorEncodings},
            {binName, maybeSize, specialSize, specialSizeOn, specialColor, specialColorOn, renderedColorStyle, renderedSizeStyle});
        */
        return (
            <div className={styles['dotContainer']}>
                <div
                    className={styles[specialSizeOn ? 'dot' : 'noDot']}
                    style={{
                        ...renderedColorStyle,
                        ...renderedSizeStyle
                    }}
                />
            </div>
        );
    };
}


function makeSizeNormalizer ({sizeEncodings, sizesOn}) {
    //console.debug('makeSizeNormalizer', {sizeEncodings, sizesOn});

    if (!sizesOn) {
        //console.debug('no sizes on, identity');
        return (v) => v;
    }

    const encodings = sizeEncodings;
    const viaFixedCategoricalMapping =
        encodings.mapping && encodings.mapping.categorical && encodings.mapping.categorical.fixed;        
    const viaBins =
        !viaFixedCategoricalMapping &&
        encodings.binning &&
        encodings.binning.bins &&
        encodings.binning.bins instanceof Array;

    let vals = [];

    if (viaBins) {
        for (let binIndex = 0; binIndex < encodings.binning.bins.length; binIndex++) {
            const bin = encodings.binning.bins[binIndex];
            for (let valIndex = 0; valIndex < bin.values.length; valIndex++) {
                if (bin.values[valIndex] === binName) {
                    vals.push(encodings.legend[binIndex]);
                }
            }
        }
    } else if (viaFixedCategoricalMapping) {
        Object.keys(encodings.mapping.categorical.fixed).forEach(key => {
            vals.push(encodings.mapping.categorical.fixed[key]);
        });
        if (encodings.mapping.categorical.other != null && encodings.mapping.categorical.other !== undefined) {
            vals.push(encodings.mapping.categorical.other);
        }
    } else {
        //console.debug('unexpected size encoding', { encodings });
        return (size) => size;
    }

    vals = vals.map(Math.abs).filter(v => !isNaN(v));

    if (vals.length === 0) {
        //console.debug('no size values found', { encodings });
        return (size) => 0.5;
    }

    const min = Math.min(...vals);
    const max = Math.max(...vals);

    if (min === max) {  
        //console.debug('all size values are the same', { encodings });
        return (size) => 0.5;
    }

    //console.debug('made size normalizer', { vals, min, max });

    return (size) => {
        if (size === undefined || isNaN(size)) {
            //console.debug('mapping invalid size', {size});
            return 0;
        }
        //console.debug('mapped size', {size, out: (size - min) / (max - min)});
        return (size - min) / (max - min);
    }
}

